import { memo } from 'react'
import RenderReactPdfViewer from '@/components/react-pdf-viewer/render-react-pdf-viewer'
import { PaxtonHighlightArea } from '@/components/react-pdf-viewer/schema/paxton-highlight-area'
import { useGcsPdfLoader } from '@/content-viewer/hooks/useGcsPdfLoader'
import ContentViewerLoadingSpinner from '../common/ContentViewerLoadingStates'
import ContentViewerErrorState from '../common/ContentViewerErrorState'

type PDFBaseRendererProps = {
  filePath: string
  gcsBucketRef: string
  legacy_always_cloud?: boolean
  highlights: PaxtonHighlightArea[]
  startingPage?: number
  title?: string // For Sentry logging
  enableRetry?: boolean // Whether to show retry button on error (defaults to true)
}

/**
 * PDF Base Renderer
 *
 * Base renderer component for displaying PDF files from GCS.
 * Uses shared useGcsPdfLoader hook for fetching and validation.
 *
 * No layout or header - only renders PDF content.
 *
 * @param props - PDF rendering configuration
 * @returns PDF viewer with loading and error states
 */
function PDFBaseRenderer(props: PDFBaseRendererProps): JSX.Element {
  const { filePath, gcsBucketRef, legacy_always_cloud = true, highlights, startingPage, title, enableRetry = true } = props

  const { loading, error, pdfFile, retry } = useGcsPdfLoader({
    filePath,
    gcsBucket: gcsBucketRef,
    legacy_always_cloud,
    sentryContext: { title, filePath },
  })

  if (loading) {
    return <ContentViewerLoadingSpinner size="small" />
  }

  if (error || !pdfFile) {
    return <ContentViewerErrorState message="Failed to load PDF file." onRetry={enableRetry ? retry : undefined} />
  }

  return <RenderReactPdfViewer pdfFile={pdfFile} highlightAreas={highlights} startingPage={startingPage} />
}

// Memoize to prevent re-rendering when props haven't changed
const PDFBaseRendererMemoized = memo(PDFBaseRenderer)
PDFBaseRendererMemoized.displayName = 'PDFBaseRenderer'

export default PDFBaseRendererMemoized
